// src/features/etf-info/api.ts
//
// Data fetcher for the ETF Info deep-dive page. Mirrors stock-info/api.ts:
// talks to the FastAPI etf_info router via the shared `apiClient` axios
// instance, which attaches the WordPress-issued JWT and bakes the `/api/v1`
// version prefix into its baseURL. We therefore request the bare
// `/etf-info/*` path here.
//
// CRITICAL: the backend serializes with `model_dump(by_alias=False,
// exclude_none=True)` -- so JSON keys are SNAKE_CASE and every field is
// optional (null fields are dropped).

import { apiClient } from '../../api/client';

const BASE_URL = '/etf-info';

// ---------------------------------------------------------------------------
// Island 1 — Overview (quote + profile + static ETF info)
// ---------------------------------------------------------------------------
export interface EtfOverview {
    symbol: string;
    quote: {
        symbol?: string;
        price?: number;
        changes_percentage?: number;
        change?: number;
        previous_close?: number;
        volume?: number;
        exchange?: string;
        name?: string;
        // ISO listing currency resolved server-side. For an ETF the fund NAV
        // currency dominates, then /stable/profile currency, then venue.
        currency?: string;
        // Average daily traded volume (share count) from /stable/profile.
        avg_volume?: number;
    };
    profile: {
        company_name?: string;
        description?: string;
        exchange?: string;
        // Enriched header stats sourced server-side from /stable/profile.
        beta?: number;
        isin?: string;
        // Trailing annual dividend per share (absolute, in listing currency).
        last_dividend?: number;
    };
    info: {
        symbol?: string;
        name?: string;
        description?: string;
        etf_company?: string;
        expense_ratio?: number;
        dividend_yield?: number;
        aum?: number;
        nav?: number;
        nav_currency?: string;
        avg_volume?: number;
        inception_date?: string;
        holdings_count?: number;
        asset_class?: string;
        domicile?: string;
        website?: string;
    };
}

export const fetchEtfOverview = async (symbol: string): Promise<EtfOverview> => {
    const { data } = await apiClient.get<EtfOverview>(`${BASE_URL}/${symbol}/overview`);
    return data;
};

// ---------------------------------------------------------------------------
// Island 2 — Intraday chart + technical indicators (identical to stock lens)
// ---------------------------------------------------------------------------
export interface ChartBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

export interface TechnicalIndicatorBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    // The active indicator arrives under its own key (e.g. `sma`, `rsi`).
    [indicator: string]: number | string;
}

export type TechnicalIndicator = 'sma' | 'ema' | 'wma' | 'rsi' | 'adx';

export const fetchIntradayChart = async (
    symbol: string,
    interval: string,
): Promise<ChartBar[]> => {
    const { data } = await apiClient.get<ChartBar[]>(`${BASE_URL}/${symbol}/chart`, {
        params: { interval },
    });
    return data;
};

export const fetchTechnicalIndicator = async (
    symbol: string,
    params: {
        indicator: TechnicalIndicator;
        periodLength: number;
        timeframe: string;
        from?: string;
        to?: string;
    },
): Promise<TechnicalIndicatorBar[]> => {
    const { data } = await apiClient.get<TechnicalIndicatorBar[]>(
        `${BASE_URL}/${symbol}/technical-indicators`,
        {
            params: {
                indicator: params.indicator,
                periodLength: params.periodLength,
                timeframe: params.timeframe,
                ...(params.from ? { from: params.from } : {}),
                ...(params.to ? { to: params.to } : {}),
            },
        },
    );
    return data;
};

// ---------------------------------------------------------------------------
// Island 3 — Top Holdings
// ---------------------------------------------------------------------------
export interface EtfHolding {
    asset?: string;
    name?: string;
    weight_percentage?: number;
    shares_number?: number;
    market_value?: number;
}

export const fetchEtfHoldings = async (
    symbol: string,
    limit = 25,
): Promise<EtfHolding[]> => {
    const { data } = await apiClient.get<EtfHolding[]>(
        `${BASE_URL}/${symbol}/holdings`,
        { params: { limit } },
    );
    return data;
};

// ---------------------------------------------------------------------------
// Island 4 — Allocations & Exposures
// ---------------------------------------------------------------------------
export interface SectorWeight {
    sector?: string;
    weight_percentage?: number;
}

export interface CountryWeight {
    country?: string;
    weight_percentage?: number;
}

export const fetchSectorWeightings = async (
    symbol: string,
): Promise<SectorWeight[]> => {
    const { data } = await apiClient.get<SectorWeight[]>(
        `${BASE_URL}/${symbol}/sector-weightings`,
    );
    return data;
};

export const fetchCountryWeightings = async (
    symbol: string,
): Promise<CountryWeight[]> => {
    const { data } = await apiClient.get<CountryWeight[]>(
        `${BASE_URL}/${symbol}/country-weightings`,
    );
    return data;
};

// ---------------------------------------------------------------------------
// Island 5 — Peers (competitor ETFs ranked by AUM)
// ---------------------------------------------------------------------------
export interface EtfPeer {
    symbol: string;
    name?: string;
    price?: number;
    changes_percentage?: number;
    aum?: number;
    expense_ratio?: number;
}

export const fetchEtfPeers = async (symbol: string): Promise<EtfPeer[]> => {
    const { data } = await apiClient.get<EtfPeer[]>(`${BASE_URL}/${symbol}/peers`);
    return data;
};
